import { useEffect, useState } from "react";

import { parseServerDate } from "@/lib/utils";

/**
 * Ticks a 1-second countdown toward a server-supplied deadline. Returns the
 * clamped milliseconds remaining (never negative), or null until `expiresAt`
 * is known. Restarts whenever the deadline changes (e.g. a section transition
 * re-points expires_at while the route stays mounted).
 *
 * Lives under `lib/` to match useModalOverlay / useModalStateReset.
 */
export function useCountdown(
  expiresAt: string | null | undefined,
): number | null {
  const [remainingMs, setRemainingMs] = useState<number | null>(null);
  useEffect(() => {
    if (!expiresAt) {
      // No deadline on THIS position — drop any value left over from the last
      // one. Without the reset the hook keeps the previous question's reading,
      // so moving from a lapsed timed question onto a clockless one (FEAT-11's
      // boundary) hands the new screen a stale `0` and every "time is up"
      // effect fires again on a page that has no clock at all.
      setRemainingMs(null);
      return;
    }
    const targetMs = parseServerDate(expiresAt).getTime();
    const tick = () => setRemainingMs(Math.max(0, targetMs - Date.now()));
    tick();
    const id = window.setInterval(tick, 1000);
    return () => window.clearInterval(id);
  }, [expiresAt]);
  return remainingMs;
}
